import { Injectable, computed, inject, signal } from '@angular/core';
import {
  Auth,
  IdTokenResult,
  OAuthProvider,
  User,
  getIdTokenResult,
  getRedirectResult,
  signInWithRedirect,
  signOut,
} from '@angular/fire/auth';
import {
  APP_ENVIRONMENT,
  AppEnvironment,
} from '@price-management-console/shared-ui';
import { JwtPayload, jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Inject config into AuthService
  private readonly environment: AppEnvironment = inject(APP_ENVIRONMENT);
  private readonly auth: Auth = inject(Auth);

  private readonly OIDC_PROVIDER = this.environment.OIDC_PROVIDER;
  private readonly EXPECTED_TENANT_ID = this.environment.TENANT_ID;

  private readonly _user = signal<User | null | undefined>(undefined); // The firebase user
  private readonly _firebaseToken = signal<string | null>(null); // The firebase JWT id token
  private readonly _tokenResult = signal<IdTokenResult | null>(null); // The firebase token result (instead of manually decoding the JWT)

  private readonly _initializationError = signal<string | null>(null);
  private readonly _samAccountName = signal<string | null>(
    sessionStorage.getItem('sam_account_name')
  );

  readonly user = this._user.asReadonly();
  readonly firebaseToken = this._firebaseToken.asReadonly();
  readonly tokenResult = this._tokenResult.asReadonly();
  readonly initializationError = this._initializationError.asReadonly();
  readonly samAccountName = this._samAccountName.asReadonly();

  isLoading = computed(() => this._user() === undefined);
  isAuthenticated = computed(() => !!this._user());
  claims = computed(() => this._tokenResult()?.claims ?? null);
  userId = computed<string | null>(() => {
    return (
      this.claims()?.firebase?.identities?.[this.OIDC_PROVIDER]?.[0] ?? null
    );
  });

  public async initialize(): Promise<void> {
    try {
      this._initializationError.set(null);
      // Need to capture redirect result to handle oidc sign redirect.
      const shouldContinue = await this.handleRedirectResult();
      if (!shouldContinue) {
        this._user.set(null);
        return;
      }

      // Wait for Firebase Auth to determine initial login state to prevent race conditions.
      await this.auth.authStateReady();

      // At this point, it's safe to get current user status. Set signals accordingly.
      const loggedInUser = this.auth.currentUser;

      this._user.set(loggedInUser);
      if (loggedInUser) {
        const tokenResult = await getIdTokenResult(loggedInUser);
        this._tokenResult.set(tokenResult); // This will automatically set the claims
        this._firebaseToken.set(tokenResult.token); // Store the id token so we can authenticate API calls
      }
      console.log(`Auth initialization successful.`);
    } catch (error: any) {
      console.error('Auth Initialization Failed:', error);
      this._initializationError.set(
        error.message || 'Unknown authentication error.'
      );
      this.signOut();
      this._user.set(null);
    }
  }

  /**
   * Checks for a redirect result after OIDC login and logs/stores the Azure tokens
   */
  private async handleRedirectResult(): Promise<boolean> {
    try {
      const result = await getRedirectResult(this.auth);
      if (!result) return true; // Should continue initialization

      const credential = OAuthProvider.credentialFromResult(result);
      if (!credential) return true;

      if (credential.idToken) {
        const isValid = await this.processIdTokenClaims(credential.idToken);
        if (!isValid) return false;
      }

      return true; // Continue initialization
    } catch (error: any) {
      return this.handleRedirectError(error);
    }
  }

  private async processIdTokenClaims(idToken: string): Promise<boolean> {
    let payload: JwtPayload & { tid?: string; sam_account_name?: string };

    try {
      payload = jwtDecode<
        JwtPayload & { tid?: string; sam_account_name?: string }
      >(idToken);
    } catch (decodeError) {
      console.error('Failed to decode Azure token', decodeError);
      return false; // Return false to stop initialization on corrupt token
    }

    // --- TENANT ID VALIDATION ---
    if (
      this.EXPECTED_TENANT_ID &&
      !this.verifyTenantId(payload, this.EXPECTED_TENANT_ID)
    ) {
      console.error(
        'Tenant ID mismatch. User does not belong to the required tenant.'
      );
      await this.signOut();
      this._initializationError.set('tenant-mismatch');
      return false; // Return false to stop initialization
    }

    // Extract sam_account_name from the Azure token
    if (payload?.sam_account_name) {
      sessionStorage.setItem('sam_account_name', payload.sam_account_name);
      this._samAccountName.set(payload.sam_account_name);
    }

    return true;
  }

  private async handleRedirectError(error: any): Promise<boolean> {
    // There was an error during the sign in
    console.error('Error handling redirect result:', error);

    // If the sign-in was blocked by the cloud function, we don't have access, so navigate to unauthorized
    if (
      error?.code === 'auth/internal-error' &&
      error?.message?.includes('HTTP Cloud Function')
    ) {
      await this.signOut();
      this._initializationError.set('auth-blocked');
      return false; // Return false to stop initialization
    }

    return true; // Continue initialization
  }

  async signOut(): Promise<void> {
    try {
      sessionStorage.removeItem('sam_account_name');
      this._samAccountName.set(null);
      await signOut(this.auth);
      console.log('User signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  async oidcLogin(providerId?: string) {
    const provider = new OAuthProvider(providerId || this.OIDC_PROVIDER);

    // Pass the specific tenant ID to Microsoft to restrict the login prompt
    if (this.EXPECTED_TENANT_ID) {
      provider.setCustomParameters({
        tenant: this.EXPECTED_TENANT_ID,
      });
    }

    return signInWithRedirect(this.auth, provider);
  }

  getAuthFirebaseUser(): User | null {
    return this.auth.currentUser;
  }

  async getIdToken(): Promise<string | null> {
    const user = this.getAuthFirebaseUser();
    if (!user) {
      return null;
    }
    return user.getIdToken();
  }

  /**
   * Helper function to check the 'tid' (Tenant ID) claim from a decoded token payload
   */
  private verifyTenantId(
    payload: { tid?: string },
    expectedTenantId: string
  ): boolean {
    return payload?.tid === expectedTenantId;
  }
}
